import clsx from 'clsx';
import { forwardRef, useCallback, useEffect, useRef, memo } from 'react';
import { useIntl } from 'react-intl';

import {
  type DataTestId,
  type StylingProps,
} from '@dynatrace/strato-components/core';

import { usePanelsContext } from './hooks/_usePanelsContext.js';
import { useParentPanelContext } from './hooks/_useParentPanelContext.js';
import { useResizable } from './hooks/useResizable.js';
import { resizeHandleCSS, resizerCSS } from './Panel.sty.js';
import { PanelsActions } from './types/panels-state.js';
import { anyUnitToFractions } from './utils/units.js';
import { useMergeRefs } from '../../../core/hooks/useMergeRefs.js';
import { useFocusRing } from '../../../core/styles/useFocusRing.js';

export const RESIZE_HANDLE_SIZE = 8;

//#region Props
/** @internal */
export type ResizerProps = {
  name: string;
  order: number;
  resizable: boolean;
} & StylingProps &
  DataTestId;
//#endregion

/**
 * The `Resizer` component must be rendered between `Panel`s to ensure correct calculations
 * and provide a handle for the user to grab for resizing panels.
 * @internal
 */
export const Resizer = memo(
  forwardRef<HTMLDivElement, ResizerProps>((props, forwardedRef) => {
    const {
      name,
      order,
      style,
      resizable,
      className: consumerClassName,
      style: consumerStyle,
      'data-testid': dataTestId,
      ...remainingProps
    }: ResizerProps = props;
    const intl = useIntl();

    const { dispatch, state } = usePanelsContext();
    const { parentPanelWidth, sizes, sortedPanels } = useParentPanelContext();

    const { focusClassName, focusProps } = useFocusRing();

    const containerRef = useRef<HTMLDivElement | null>(null);
    const { resizerRef } = useResizable({
      order,
    });

    const calculateHandlePosition = useCallback(() => {
      const panelsBeforeHandle = sortedPanels.slice(0, order + 1);
      let handlePosition = 0;
      for (const panel of panelsBeforeHandle) {
        handlePosition +=
          anyUnitToFractions(sizes[panel.name], parentPanelWidth, 0) || 0;
      }

      return Math.ceil(handlePosition);
      // Excluding sortedPanels to avoid additional re-renders.
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [order, parentPanelWidth, sizes]);

    // Current position of the resize handle in the container to populate aria-valuenow.
    const currentPosition = calculateHandlePosition();

    const mergedRef = useMergeRefs([forwardedRef, resizerRef]);

    useEffect(() => {
      dispatch({
        type: PanelsActions.ADDRESIZEHANDLE,
        payload: {
          name,
          order,
        },
      });

      return () => {
        dispatch({
          type: PanelsActions.REMOVERESIZEHANDLE,
          payload: {
            name,
          },
        });
      };
      // Only needed for cleanup to remove handle when unmounting to enable conditional rendering
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const isRegistered = state.resizeHandles[name] !== undefined;

    return (
      <div
        ref={containerRef}
        className={clsx(consumerClassName, resizerCSS)}
        style={{
          ...style,
          ...consumerStyle,
          gridArea: name,
          gridRow: '2',
          width: isRegistered ? undefined : 0,
          display: isRegistered ? undefined : 'none',
        }}
        data-testid={dataTestId}
        {...(!resizable && { role: 'separator' })}
        {...remainingProps}
      >
        {resizable && (
          <>
            <div
              tabIndex={0}
              ref={mergedRef}
              data-testid="panel-resize-handle"
              role="separator"
              className={clsx(resizeHandleCSS, focusClassName)}
              aria-valuenow={currentPosition}
              aria-label={intl.formatMessage(
                {
                  defaultMessage:
                    'Button "{name}" for resizing the corresponding panels.',
                  id: '5mSdhujC0nLrjORk',
                  description: 'Aria label for the panel resizer.',
                },
                { name },
              )}
              aria-describedby="resize-handle-description"
              {...focusProps}
            />
            <p id="resize-handle-description" hidden>
              {intl.formatMessage({
                defaultMessage:
                  'The min and max value represent the leftmost / rightmost border of the container element. The valuenow equals the percentage position of the resize handle inside the container. It can not necessarily reach the min / max position.',
                id: 'O4kuGPSNH0oBronn',
                description:
                  "Additional description for the resize handle's position.",
              })}
            </p>
          </>
        )}
      </div>
    );
  }),
);
